// src/hooks/brandCustomization/usePublicPageCustomization.js
import { useState, useEffect } from 'react';
import publicPageCustomizationService from '../../helpers/publicPageCustomizationHelper';
import { defaultPublicPageTexts } from '../../utils/brandCustomization';
import FindTicketPageContent from '../../components/public/FindTicketPageContent';
import ProductosPublicPageContent from '../../components/public/ProductosPublicPageContent';
import QRRegisterPageContent from '../../components/public/QRRegisterPageContent';
import Swal from 'sweetalert2';

/**
 * Hook para manejar la personalización de páginas públicas
 * @param {string} marcaId - ID de la marca
 * @returns {object} Estado y funciones para manejar public page customization
 */
export const usePublicPageCustomization = (marcaId) => {
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState({});

    // Estilos por defecto para páginas públicas
    const defaultPublicPagesStyles = {
        findTicket: {
            headerBackgroundColor: '#4F46E5',
            headerTextColor: '#FFFFFF',
            pageBackgroundColor: '#F9FAFB',
            cardBackgroundColor: '#FFFFFF',
            cardBorderRadius: '16px',
            titleColor: '#111827',
            descriptionColor: '#4B5563',
            inputBorderColor: '#D1D5DB',
            inputFocusColor: '#3B82F6',
            buttonBackgroundColor: '#4F46E5',
            buttonTextColor: '#FFFFFF',
            buttonHoverColor: '#4338CA',
            errorBackgroundColor: '#FEF3C7',
            errorBorderColor: '#FCD34D',
            errorTextColor: '#92400E',
            fontFamily: 'sans-serif',
            fontSize: '16px'
        },
        productos: {
            headerBackgroundColor: '#4F46E5',
            headerTextColor: '#FFFFFF',
            categoryTabActiveColor: '#2563EB',
            categoryTabInactiveColor: '#F3F4F6',
            productCardBackgroundColor: '#FFFFFF',
            productCardHoverColor: '#F9FAFB',
            productNameColor: '#111827',
            productDescriptionColor: '#6B7280',
            productPriceColor: '#2563EB',
            floatingButtonBackgroundColor: '#2563EB',
            floatingButtonTextColor: '#FFFFFF',
            modalBackgroundColor: '#FFFFFF',
            modalOverlayColor: 'rgba(0, 0, 0, 0.5)',
            benefitCardBackgroundColor: '#EFF6FF',
            benefitIconColor: '#2563EB',
            fontFamily: 'sans-serif',
            fontSize: '16px'
        },
        qrRegister: {
            pageBackgroundColor: '#EFF6FF',
            headerBackgroundColor: '#FFFFFF',
            headerBorderColor: '#2563EB',
            cardBackgroundColor: '#FFFFFF',
            cardBorderRadius: '8px',
            titleColor: '#111827',
            descriptionColor: '#4B5563',
            benefitCardBackgroundColor: '#EFF6FF',
            benefitIconBackgroundColor: '#DBEAFE',
            benefitIconColor: '#2563EB',
            inputBorderColor: '#D1D5DB',
            inputFocusColor: '#2563EB',
            buttonBackgroundColor: '#2563EB',
            buttonTextColor: '#FFFFFF',
            buttonHoverColor: '#1D4ED8',
            sectionBackgroundColor: '#F9FAFB',
            fontFamily: 'sans-serif',
            fontSize: '16px'
        }
    };

    // Config por defecto para páginas públicas
    const defaultPublicPagesConfig = {
        productos: {
            showWelcomeModal: true,
            welcomeModalDelay: 30,
            showSearchButton: true,
            showFloatingButton: true,
            productsPerCategoryPreview: 3
        }
    };

    // Merge de estilos con defaults
    const mergePublicPageStyles = (pageName, customStyles) => {
        if (!customStyles) return defaultPublicPagesStyles[pageName];
        return {
            ...defaultPublicPagesStyles[pageName],
            ...customStyles
        };
    };

    // Cargar datos del backend
    const fetchData = async () => {
        if (!marcaId) return;

        setLoading(true);
        try {
            const publicPagesData = await publicPageCustomizationService.getCustomizations(marcaId);
            const publicPagesMap = {};

            const pagesArray = publicPagesData.pages || publicPagesData.customizations || [];
            if (Array.isArray(pagesArray)) {
                pagesArray.forEach(page => {
                    const pageData = page._doc || page;
                    const pageName = pageData.pageName || page.pageName;

                    if (!pageName) return;

                    publicPagesMap[pageName] = {
                        pageName: pageName,
                        layout: pageData.layout || page.layout,
                        layoutOptions: pageData.layoutOptions || page.layoutOptions || {},
                        isActive: pageData.isActive !== undefined ? pageData.isActive : page.isActive,
                        texts: pageData.texts || page.texts || {},
                        styles: pageData.styles || page.styles || {},
                        config: pageData.config || page.config || {}
                    };
                });
            }

            // Inicializar formData
            const pages = ['findTicket', 'productos', 'qrRegister'];
            const initialFormData = {};

            pages.forEach(pageName => {
                initialFormData[pageName] = {
                    texts: {
                        ...defaultPublicPageTexts[pageName],
                        ...(publicPagesMap[pageName]?.texts || {})
                    },
                    styles: mergePublicPageStyles(pageName, publicPagesMap[pageName]?.styles),
                    layout: publicPagesMap[pageName]?.layout || (pageName === 'findTicket' ? 'centered' : pageName === 'productos' ? 'grid' : 'classic'),
                    layoutOptions: publicPagesMap[pageName]?.layoutOptions || {},
                    config: publicPagesMap[pageName]?.config || {},
                    isActive: publicPagesMap[pageName]?.isActive !== false
                };
            });

            setFormData(initialFormData);
        } catch (error) {
            if (error.status !== 404) {
                console.error('Error cargando personalizaciones de páginas públicas:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudieron cargar las personalizaciones de páginas públicas',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [marcaId]);

    // Actualizar un campo específico
    const updateField = (pageName, field, value) => {
        setFormData(prev => {
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                    ...prev,
                    [pageName]: {
                        ...prev[pageName],
                        [parent]: {
                            ...prev[pageName][parent],
                            [child]: value
                        }
                    }
                };
            }

            return {
                ...prev,
                [pageName]: {
                    ...prev[pageName],
                    [field]: value
                }
            };
        });
    };

    // Toggle activación
    const toggleActive = async (pageName) => {
        const currentActive = formData[pageName]?.isActive;
        const newActive = !currentActive;

        updateField(pageName, 'isActive', newActive);

        try {
            await publicPageCustomizationService.updateCustomization(marcaId, pageName, {
                isActive: newActive
            });

            Swal.fire({
                title: newActive ? 'Personalización Activada' : 'Personalización Desactivada',
                text: newActive
                    ? 'La página usará la personalización personalizada'
                    : 'La página usará los valores por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            updateField(pageName, 'isActive', currentActive);

            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo actualizar el estado',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Guardar personalización
    const save = async (pageName) => {
        setIsSaving(prev => ({ ...prev, [pageName]: true }));

        try {
            const pageData = formData[pageName];
            const customizationData = {
                texts: pageData.texts,
                styles: pageData.styles,
                config: pageData.config,
                layout: pageData.layout || (pageName === 'findTicket' ? 'centered' : pageName === 'productos' ? 'grid' : 'classic'),
                layoutOptions: pageData.layoutOptions,
                isActive: pageData.isActive
            };

            try {
                await publicPageCustomizationService.updateCustomization(marcaId, pageName, customizationData);
            } catch (updateError) {
                if (updateError.status === 404) {
                    await publicPageCustomizationService.createCustomization(marcaId, [{
                        pageName,
                        ...customizationData
                    }]);
                } else {
                    throw updateError;
                }
            }

            Swal.fire({
                title: '¡Guardado!',
                text: 'La personalización se guardó correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            await fetchData();
        } catch (error) {
            console.error('Error guardando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo guardar la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        } finally {
            setIsSaving(prev => ({ ...prev, [pageName]: false }));
        }
    };

    // Resetear
    const reset = async (pageName) => {
        const result = await Swal.fire({
            title: '¿Resetear Personalización?',
            text: 'Se perderán todos los cambios personalizados',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, resetear',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            await publicPageCustomizationService.deleteCustomization(marcaId, pageName);

            Swal.fire({
                title: '¡Reseteado!',
                text: 'Se restauraron los valores por defecto',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });

            await fetchData();
        } catch (error) {
            console.error('Error reseteando personalización:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo resetear la personalización',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    // Función para procesar variables en textos
    const processVariables = (text, variables) => {
        if (!text || typeof text !== 'string') return text || '';
        let processed = text;
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            processed = processed.replace(regex, variables[key]);
        });
        return processed;
    };

    // Función para generar preview de página pública
    const preview = (pageName, marca) => {
        return new Promise((resolve, reject) => {
            try {
                const pageData = formData[pageName];
                if (!pageData) {
                    reject(new Error(`No hay datos para la página ${pageName}`));
                    return;
                }

                const defaultTexts = defaultPublicPageTexts[pageName];
                if (!defaultTexts) {
                    reject(new Error(`No hay textos por defecto para la página ${pageName}`));
                    return;
                }

                // Preparar variables de muestra
                const sampleVariables = {
                    brandName: marca?.nombre || 'Tu Marca',
                    brandSlogan: marca?.eslogan || 'Tu eslogan aquí',
                    tiendaNombre: 'Tienda Centro',
                    tiendaDescripcion: 'Descripción de la tienda de ejemplo',
                    tiendaDireccion: 'Calle Ejemplo 123, Ciudad, País'
                };

                // Procesar textos con variables
                const processedTexts = {};
                Object.keys(defaultTexts).forEach(key => {
                    const text = pageData.texts[key] || defaultTexts[key];
                    processedTexts[key] = processVariables(text, sampleVariables);
                });

                // Obtener estilos (combinar personalizados con defaults)
                const styles = {
                    ...defaultPublicPagesStyles[pageName],
                    ...pageData.styles
                };

                // Generar preview según la página
                const layout = pageData.layout || (pageName === 'findTicket' ? 'centered' : pageName === 'productos' ? 'grid' : 'classic');
                const layoutOptions = pageData.layoutOptions || {};

                // Obtener config (combinar personalizados con defaults)
                const defaultConfig = defaultPublicPagesConfig[pageName] || {};
                const config = {
                    ...defaultConfig,
                    ...(pageData.config || {})
                };

                let component = null;

                // Usar el mismo componente React en lugar de HTML estático
                if (pageName === 'findTicket') {
                    component = (
                        <FindTicketPageContent
                            marca={marca}
                            texts={processedTexts}
                            styles={styles}
                            layout={layout}
                            layoutOptions={layoutOptions}
                            imagenFondo={marca?.imagenFondo || null}
                            isPreview={true}
                        />
                    );
                } else if (pageName === 'productos') {
                    // Datos de muestra para productos y categorías (para preview)
                    const sampleProductos = [
                        {
                            _id: '1',
                            nombre: 'Producto Ejemplo 1',
                            descripcion: 'Descripción del producto de ejemplo',
                            precio: 9.99,
                            imageUrl: null,
                            categoria: { _id: 'cat1' }
                        },
                        {
                            _id: '2',
                            nombre: 'Producto Ejemplo 2',
                            descripcion: 'Otra descripción de ejemplo',
                            precio: 12.50,
                            imageUrl: null,
                            categoria: { _id: 'cat1' }
                        }
                    ];
                    const sampleCategorias = [
                        {
                            _id: 'cat1',
                            nombre: 'Categoría Ejemplo'
                        }
                    ];

                    component = (
                        <ProductosPublicPageContent
                            marca={marca}
                            productos={sampleProductos}
                            categorias={sampleCategorias}
                            texts={processedTexts}
                            styles={styles}
                            layout={layout}
                            layoutOptions={layoutOptions}
                            config={config}
                            isPreview={true}
                        />
                    );
                } else if (pageName === 'qrRegister') {
                    // Datos de muestra para la tienda (para preview)
                    const sampleTienda = {
                        _id: 'sample-tienda',
                        nombre: sampleVariables.tiendaNombre || 'Tienda Centro',
                        descripcion: sampleVariables.tiendaDescripcion || 'Descripción de la tienda de ejemplo',
                        via: 'Calle',
                        calle: 'Ejemplo',
                        numero: '123',
                        poblacion: 'Ciudad',
                        pais: 'País',
                        region: 'Región',
                        logotipo: marca?.logotipo || null,
                        marca: marca?._id || marca?.id || 'sample-marca'
                    };

                    component = (
                        <QRRegisterPageContent
                            tienda={sampleTienda}
                            texts={processedTexts}
                            styles={styles}
                            layout={layout}
                            layoutOptions={layoutOptions}
                            imagenFondo={marca?.imagenFondo || null}
                            isPreview={true}
                        />
                    );
                }

                if (!component) {
                    reject(new Error(`No se pudo generar preview para ${pageName}`));
                    return;
                }

                resolve({ component, html: null });
            } catch (error) {
                console.error('Error generando preview:', error);
                reject(error);
            }
        });
    };

    return {
        formData,
        loading,
        isSaving,
        updateField,
        toggleActive,
        save,
        reset,
        fetchData,
        preview
    };
};

export default usePublicPageCustomization;

